/**
 * ConsequencePreview — Non-HR UX layer (HUMANAI_UX_NONHR.md §3, หลักการ 7).
 * Ported 1:1 from PayPilot apps/liff.
 *
 * "กล้ากด — เห็นผลลัพธ์ก่อนยืนยัน" — พี่ที่รวยกว่าของ useConfirm(): แทนถาม
 * "ยืนยันไหม?" ลอยๆ → สรุปให้เห็นว่า "กดแล้วจะเกิดอะไร" (ตัวเลข + สิ่งที่จะเกิด)
 * + เตือน compliance ถ้ามี.
 *
 *   const consequence = useConsequence();
 *   const ok = await consequence({ title, lines, effects, alerts });
 *   if (!ok) return;
 *
 * Mount <ConsequenceProvider> ครั้งเดียวใกล้ root (คู่กับ ConfirmProvider).
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { ComplianceAlertList, type ComplianceItem } from './ComplianceAlert';

export interface ConsequenceLine {
  label: string;
  value: string;
  /** เน้นเป็นบรรทัดหลัก (ตัวใหญ่ขึ้น) */
  emphasis?: boolean;
  /** money = ตัวเลขเงิน tabular · danger = สีแดง */
  tone?: 'default' | 'money' | 'danger';
}

export interface ConsequenceOpts {
  title: string;
  /** ประโยคนำ — default "เมื่อกดยืนยัน ระบบจะดำเนินการต่อไปนี้" */
  intro?: ReactNode;
  /** บรรทัดสรุปตัวเลข */
  lines?: ConsequenceLine[];
  /** รายการผลข้างเคียง bullet */
  effects?: string[];
  /** เตือน compliance แทรกก่อนปุ่มยืนยัน */
  alerts?: ComplianceItem[];
  confirmText?: string;
  cancelText?: string;
  /** ปุ่มยืนยันสีแดง (action ย้อนยาก) */
  danger?: boolean;
}

type ConsequenceFn = (opts: ConsequenceOpts) => Promise<boolean>;

const Ctx = createContext<ConsequenceFn | null>(null);

export function useConsequence(): ConsequenceFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useConsequence must be used within <ConsequenceProvider>');
  return ctx;
}

const TONE_CLASS: Record<NonNullable<ConsequenceLine['tone']>, string> = {
  default: 'text-gray-900',
  money: 'text-gray-900 tabular-nums',
  danger: 'text-red-700 tabular-nums',
};

/** Presentational summary — ใช้เดี่ยวในหน้าได้ (ไม่ผ่าน modal) */
export function ConsequenceBody({
  intro,
  lines,
  effects,
  alerts,
}: Omit<ConsequenceOpts, 'title' | 'confirmText' | 'cancelText' | 'danger'>) {
  const hasBlock = (alerts ?? []).some((a) => a.level === 'block');
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{intro ?? 'เมื่อกดยืนยัน ระบบจะดำเนินการต่อไปนี้'}</p>

      {lines && lines.length > 0 && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 divide-y divide-gray-200">
          {lines.map((ln, i) => (
            <div key={i} className="flex items-baseline justify-between px-3 py-2 gap-3">
              <span className={`text-gray-600 ${ln.emphasis ? 'text-sm font-medium' : 'text-sm'}`}>{ln.label}</span>
              <span
                className={`${TONE_CLASS[ln.tone ?? 'default']} ${
                  ln.emphasis ? 'text-lg font-bold' : 'text-sm font-semibold'
                }`}
              >
                {ln.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {effects && effects.length > 0 && (
        <ul className="space-y-1">
          {effects.map((e, i) => (
            <li key={i} className="text-sm text-gray-700 flex gap-2">
              <span className="text-gray-400">•</span>
              <span>{e}</span>
            </li>
          ))}
        </ul>
      )}

      {alerts && alerts.length > 0 && <ComplianceAlertList items={alerts} />}
      {hasBlock && <p className="text-xs text-red-600">มีรายการที่ต้องแก้ก่อน — ตรวจสอบก่อนดำเนินการต่อ</p>}
    </div>
  );
}

export function ConsequenceProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConsequenceOpts | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const consequence = useCallback<ConsequenceFn>((o) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (v: boolean) => {
    resolver.current?.(v);
    resolver.current = null;
    setOpts(null);
  };

  return (
    <Ctx.Provider value={consequence}>
      {children}
      <Modal open={!!opts} onClose={() => close(false)} title={opts?.title}>
        {opts && (
          <>
            <ConsequenceBody intro={opts.intro} lines={opts.lines} effects={opts.effects} alerts={opts.alerts} />
            <div className="flex gap-2 mt-5">
              <button className="btn-secondary flex-1" onClick={() => close(false)}>
                {opts.cancelText ?? 'ยกเลิก'}
              </button>
              <button className={`flex-1 ${opts.danger ? 'btn-danger' : 'btn-cta'}`} onClick={() => close(true)}>
                {opts.confirmText ?? 'ยืนยัน'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </Ctx.Provider>
  );
}
